import type {
  AvailabilityQueryFilters,
  AvailabilityRule,
  AvailabilityRuleInput,
} from '@/admin/hooks/use-availability-rules'
import type { Product } from '@/admin/types/products'
import type { WarrantyTemplate } from '@/admin/types/warranties'

export interface PolicyGroup {
  policyCode: string
  rules: AvailabilityRule[]
  warrantyTypes: string[]
  templateNames: string[]
  startDate: string | null
  endDate: string | null
  priority: number
  mappedProducts: Product[]
}

const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: 'Manufacturer',
  EXTENDED_WARRANTY: 'Extended',
  RETAILER_WARRANTY: 'Retailer',
}

export interface WarrantyRouting {
  warranty_template_uuid: string
  warranty_template_option_uuid: string
  policy_code: string
  priority: number
  attribute_values: Record<string, unknown>
}

export const EMPTY_ROUTING: WarrantyRouting = {
  warranty_template_uuid: '',
  warranty_template_option_uuid: '',
  policy_code: '',
  priority: 0,
  attribute_values: {},
}

export function isStringRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

export function readRouting(
  effect_overrides: Record<string, unknown> | null | undefined,
): WarrantyRouting {
  const src = isStringRecord(effect_overrides) ? effect_overrides : {}
  const attrs = isStringRecord(src.attribute_values) ? src.attribute_values : {}
  return {
    warranty_template_uuid:
      typeof src.warranty_template_uuid === 'string' ? src.warranty_template_uuid : '',
    warranty_template_option_uuid:
      typeof src.warranty_template_option_uuid === 'string'
        ? src.warranty_template_option_uuid
        : '',
    policy_code: typeof src.policy_code === 'string' ? src.policy_code : '',
    priority: typeof src.priority === 'number' ? src.priority : 0,
    attribute_values: { ...attrs },
  }
}

export function routingToEffectOverrides(
  routing: WarrantyRouting,
): Record<string, unknown> {
  if (!routing.warranty_template_uuid || !routing.warranty_template_option_uuid) {
    return {}
  }
  const payload: Record<string, unknown> = {
    warranty_template_uuid: routing.warranty_template_uuid,
    warranty_template_option_uuid: routing.warranty_template_option_uuid,
    policy_code: routing.policy_code.trim(),
  }
  if (routing.priority) payload.priority = routing.priority
  if (Object.keys(routing.attribute_values).length > 0) {
    payload.attribute_values = routing.attribute_values
  }
  return payload
}

export function countQueryTargets(filters: AvailabilityQueryFilters): number {
  return (
    (filters.deny_all ? 1 : 0) +
    (filters.warranty_types?.length ?? 0) +
    (filters.category_ids?.length ?? 0) +
    (filters.template_ids?.length ?? 0) +
    (filters.attribute_filters?.length ?? 0) +
    (filters.geo_zone_ids?.length ?? 0)
  )
}

export function toggleStringInArray(
  values: string[] | undefined,
  value: string,
): string[] {
  const current = new Set(values ?? [])
  if (current.has(value)) current.delete(value)
  else current.add(value)
  return [...current]
}

function readProductAttributeValue(
  product: Product,
  codeName: string,
): unknown {
  const attrValue = product.attribute_values?.find(
    (entry) => entry.code_name === codeName,
  )?.value
  if (attrValue !== undefined) return attrValue
  return product.product_data?.[codeName]
}

export function matchProductsForRule(
  products: Product[],
  rule: AvailabilityRule,
): Product[] {
  if (rule.scope_id) {
    const scopedProduct = products.find((product) => product.uuid === rule.scope_id)
    return scopedProduct ? [scopedProduct] : []
  }
  const filters = rule.query_filters ?? {}
  if (filters.deny_all) return []
  return products.filter((product) => {
    if (
      (filters.warranty_types?.length ?? 0) > 0 &&
      !filters.warranty_types?.includes(product.status)
    ) {
      return false
    }
    if (
      (filters.category_ids?.length ?? 0) > 0 &&
      !filters.category_ids?.includes(product.category?.uuid ?? '')
    ) {
      return false
    }
    if (
      (filters.template_ids?.length ?? 0) > 0 &&
      !filters.template_ids?.includes(product.product_template?.uuid ?? '')
    ) {
      return false
    }
    if ((filters.attribute_filters?.length ?? 0) > 0) {
      const attrMatches = filters.attribute_filters?.every((filter) => {
        const value = readProductAttributeValue(product, filter.code_name)
        return String(value ?? '') === String(filter.value ?? '')
      })
      if (!attrMatches) return false
    }
    return true
  })
}

export function getPolicyCode(rule: AvailabilityRule): string {
  return readRouting(rule.effect_overrides).policy_code.trim()
}

export function hasRequiredPolicyRouting(rule: AvailabilityRule): boolean {
  const routing = readRouting(rule.effect_overrides)
  return Boolean(
    routing.policy_code.trim() &&
      routing.warranty_template_uuid &&
      routing.warranty_template_option_uuid,
  )
}

export function hasPolicyCode(rule: AvailabilityRule): boolean {
  return Boolean(getPolicyCode(rule))
}

export function makePolicyRuleCloneInput(
  rule: AvailabilityRule,
  nextPolicyCode: string,
): AvailabilityRuleInput {
  return makePolicyRuleProductCloneInput(
    rule,
    nextPolicyCode,
    rule.scope_id,
  )
}

export function makePolicyRuleProductCloneInput(
  rule: AvailabilityRule,
  nextPolicyCode: string,
  productUuid: string | null | undefined,
): AvailabilityRuleInput {
  const routing = readRouting(rule.effect_overrides)
  return {
    name: rule.name,
    description: rule.description,
    conditions: rule.conditions,
    severity: rule.severity,
    message: rule.message,
    is_active: rule.is_active,
    priority: rule.priority,
    scope: 'product',
    scope_id: productUuid ?? null,
    query_filters: rule.query_filters,
    effective_from: rule.effective_from,
    effective_until: rule.effective_until,
    effect_overrides: routingToEffectOverrides({
      ...routing,
      policy_code: nextPolicyCode.trim(),
    }),
  }
}

/**
 * Clone an entire policy group as a new policy: copies every rule in the group,
 * keeps the same product mapping (scope_id / query_filters) and warranty
 * routing, and rewrites the policy code. Used by both "Clone" and
 * "New for next year" actions on the policies page.
 */
export function makePolicyGroupCloneInputs(
  group: PolicyGroup,
  nextPolicyCode: string,
): AvailabilityRuleInput[] {
  return group.rules.map((rule) =>
    makePolicyRuleCloneInput(rule, nextPolicyCode),
  )
}

export function suggestNextYearPolicyCode(policyCode: string): string {
  // Only bump a 20XX token when it stands alone (delimited by non-digits or
  // the start/end of the string). Otherwise codes like `10020608M` would have
  // their middle digits read as a year and silently rewritten.
  const match = policyCode.match(/(?<![0-9])(20\d{2})(?![0-9])(?!.*(?<![0-9])20\d{2}(?![0-9]))/)
  if (!match) return policyCode
  const currentYear = Number(match[1])
  return policyCode.replace(match[1], String(currentYear + 1))
}

export function buildPolicyGroups(
  rules: AvailabilityRule[],
  products: Product[],
  templateByUuid: Map<string, WarrantyTemplate>,
): PolicyGroup[] {
  const grouped = new Map<string, AvailabilityRule[]>()
  for (const rule of rules) {
    if (rule.scope !== 'product') continue
    if (!hasPolicyCode(rule)) continue
    const policyCode = getPolicyCode(rule)
    if (!grouped.has(policyCode)) grouped.set(policyCode, [])
    grouped.get(policyCode)?.push(rule)
  }

  const groups: PolicyGroup[] = []
  for (const [policyCode, groupedRules] of grouped.entries()) {
    const mappedProductsMap = new Map<string, Product>()
    const templateNames = new Set<string>()
    const warrantyTypes = new Set<string>()
    let startDate: string | null = null
    let endDate: string | null = null
    let priority = 0

    for (const rule of groupedRules) {
      for (const product of matchProductsForRule(products, rule)) {
        mappedProductsMap.set(product.uuid, product)
      }
      const routing = readRouting(rule.effect_overrides)
      const template = templateByUuid.get(routing.warranty_template_uuid)
      if (template) {
        templateNames.add(template.name)
        warrantyTypes.add(
          WARRANTY_TYPE_LABELS[template.warranty_type] ?? template.warranty_type,
        )
      }
      if (rule.effective_from && (!startDate || rule.effective_from < startDate)) {
        startDate = rule.effective_from
      }
      if (rule.effective_until && (!endDate || rule.effective_until > endDate)) {
        endDate = rule.effective_until
      }
      priority = Math.max(priority, routing.priority || rule.priority)
    }

    groups.push({
      policyCode,
      rules: groupedRules.sort((a, b) => b.priority - a.priority),
      warrantyTypes: [...warrantyTypes],
      templateNames: [...templateNames],
      startDate,
      endDate,
      priority,
      mappedProducts: [...mappedProductsMap.values()].sort((a, b) =>
        a.cached_short_name.localeCompare(b.cached_short_name),
      ),
    })
  }

  // Latest availability window first; null start dates fall to the bottom.
  groups.sort((a, b) => {
    if (a.startDate && b.startDate) return b.startDate.localeCompare(a.startDate)
    if (a.startDate) return -1
    if (b.startDate) return 1
    return a.policyCode.localeCompare(b.policyCode)
  })

  return groups
}

/**
 * Return every rule in a policy group whose mapping resolves to the given
 * product. A single product can be covered by multiple rules in the same
 * policy (e.g. one per property type), so removing a "product mapping" means
 * deleting all of them at once.
 */
export function rulesForProductInGroup(
  group: PolicyGroup,
  productUuid: string,
  products: Product[],
): AvailabilityRule[] {
  return group.rules.filter((rule) =>
    matchProductsForRule(products, rule).some((p) => p.uuid === productUuid),
  )
}
